import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { format, subDays } from 'date-fns';
import { PieChart as PieChartIcon, Filter } from 'lucide-react';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip as RechartsTooltip, Legend } from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { fetchCallTopics } from '../api';
import { GRAPH_REFRESH_INTERVAL_MS } from '../config/constants';
import useTimeZone from '../hooks/useTimeZone';
import { formatDayMonthYear } from '../utils/dateTime';
import {
  CALL_CLASSIFICATION_LABELS,
  CALL_CLASSIFICATION_COLORS,
  formatClassificationType
} from '../utils/callClassification';

const defaultRange = {
  from: subDays(new Date(), 29),
  to: new Date()
};

const UNKNOWN_KEY = '__unknown';

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const ButtonReload = ({ loading, onReload }) => (
  <button
    type="button"
    onClick={onReload}
    disabled={loading}
    className="inline-flex items-center rounded-md border px-3 py-1 text-sm text-muted-foreground hover:bg-muted disabled:opacity-50"
  >
    <Filter className="mr-2 h-4 w-4" />
    {loading ? 'Обновление...' : 'Обновить'}
  </button>
);

const CallTopicsChart = ({ dateRange, callType = 'all' }) => {
  const [slices, setSlices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [chartSize, setChartSize] = useState({ width: 0, height: 0 });
  const isFetching = useRef(false);
  const chartContainerRef = useRef(null);
  const { timeZone } = useTimeZone();

  const activeDateRange = useMemo(() => {
    if (dateRange?.from && dateRange?.to) {
      return dateRange;
    }
    return defaultRange;
  }, [dateRange]);

  const dateRangeLabel = useMemo(() => {
    if (!activeDateRange?.from || !activeDateRange?.to) {
      return 'за выбранный период';
    }
    try {
      const fromLabel = formatDayMonthYear(activeDateRange.from, timeZone);
      const toLabel = formatDayMonthYear(activeDateRange.to, timeZone);
      return `за период ${fromLabel} — ${toLabel}`;
    } catch {
      return 'за выбранный период';
    }
  }, [activeDateRange, timeZone]);

  const load = useCallback(async (showSpinner = true) => {
    if (!activeDateRange?.from || !activeDateRange?.to || isFetching.current) {
      return;
    }

    if (showSpinner) {
      setIsLoading(true);
    }

    isFetching.current = true;
    setError(null);
    try {
      const params = {
        date_from: format(activeDateRange.from, 'yyyy-MM-dd'),
        date_to: format(activeDateRange.to, 'yyyy-MM-dd'),
        call_type: callType === 'all' ? undefined : callType
      };
      const data = await fetchCallTopics(params);
      setSlices(Array.isArray(data) ? data : []);
    } catch {
      setError('Не удалось загрузить распределение тем звонков.');
    } finally {
      isFetching.current = false;
      if (showSpinner) {
        setIsLoading(false);
      }
    }
  }, [activeDateRange, callType]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      load(false);
    }, GRAPH_REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [load]);

  useEffect(() => {
    const element = chartContainerRef.current;
    if (!element) {
      return undefined;
    }

    const updateSize = () => {
      setChartSize({
        width: element.clientWidth,
        height: element.clientHeight
      });
    };

    updateSize();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateSize);
      return () => window.removeEventListener('resize', updateSize);
    }

    const observer = new ResizeObserver(([entry]) => {
      const rect = entry?.contentRect;
      setChartSize({
        width: rect?.width ?? element.clientWidth,
        height: rect?.height ?? element.clientHeight
      });
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const { chartData, total } = useMemo(() => {
    const aggregated = new Map();
    let sum = 0;
    for (const slice of slices) {
      const rawKey = (slice.classificationType ?? '').trim().toLowerCase();
      const isKnown = rawKey && Object.prototype.hasOwnProperty.call(CALL_CLASSIFICATION_LABELS, rawKey);
      const key = isKnown ? rawKey : UNKNOWN_KEY;
      const calls = Number(slice.calls) || 0;
      sum += calls;
      aggregated.set(key, (aggregated.get(key) ?? 0) + calls);
    }

    const data = Array.from(aggregated.entries())
      .filter(([, value]) => value > 0)
      .map(([key, value]) => ({
        key,
        name: key === UNKNOWN_KEY ? 'Без темы' : formatClassificationType(key),
        value,
        color: CALL_CLASSIFICATION_COLORS[key] ?? CALL_CLASSIFICATION_COLORS.__unknown
      }))
      .sort((a, b) => b.value - a.value);

    return { chartData: data, total: sum };
  }, [slices]);

  const chartLayout = useMemo(() => {
    const width = chartSize.width || 320;
    const height = chartSize.height || 224;
    const legendHeight = width < 320 ? 78 : width < 420 ? 64 : 48;
    const horizontalPadding = width < 320 ? 36 : 52;
    const topGap = 16;
    const bottomGap = 8;
    const availableHeight = height - legendHeight - topGap - bottomGap;
    const outerRadius = clamp(
      Math.floor(Math.min((width - horizontalPadding) / 2, availableHeight / 2)),
      34,
      90
    );
    const centerY = topGap + outerRadius;

    return {
      centerY,
      innerRadius: Math.round(outerRadius * 0.6),
      legendHeight,
      outerRadius
    };
  }, [chartSize]);

  const TopicTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const entry = payload[0];
    const value = entry?.value ?? 0;
    const percent = total > 0 ? Math.round((value / total) * 100) : 0;
    return (
      <div
        className="rounded-md border px-3 py-2 text-sm shadow-md"
        style={{
          backgroundColor: 'hsl(var(--card))',
          color: 'hsl(var(--card-foreground))',
          borderColor: 'hsl(var(--border))'
        }}
      >
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: entry.payload?.color }} />
          <span>
            {entry.payload?.name}: {value.toLocaleString('ru-RU')} ({percent}%)
          </span>
        </div>
      </div>
    );
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div>
          <CardTitle className="flex items-center gap-2">
            <PieChartIcon className="h-5 w-5" />
            Темы звонков
          </CardTitle>
          <CardDescription>Распределение {dateRangeLabel}</CardDescription>
        </div>
        <ButtonReload loading={isLoading} onReload={load} />
      </CardHeader>
      <CardContent>
        {error && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {error}
          </div>
        )}
        {total === 0 && !isLoading && !error && (
          <p className="text-sm text-muted-foreground">Нет данных за выбранный период.</p>
        )}
        {total > 0 && (
          <div ref={chartContainerRef} className="relative h-64 w-full min-w-0 rounded-md bg-muted/30 px-2 py-4">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={160}>
              <PieChart margin={{ top: 0, right: 0, bottom: 4, left: 0 }}>
                <Pie
                  data={chartData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy={chartLayout.centerY}
                  innerRadius={chartLayout.innerRadius}
                  outerRadius={chartLayout.outerRadius}
                  paddingAngle={2}
                  isAnimationActive={false}
                >
                  {chartData.map((entry) => (
                    <Cell key={entry.key} fill={entry.color} stroke="hsl(var(--card))" />
                  ))}
                </Pie>
                <RechartsTooltip wrapperStyle={{ zIndex: 30 }} content={<TopicTooltip />} />
                <Legend
                  align="center"
                  verticalAlign="bottom"
                  height={chartLayout.legendHeight}
                  wrapperStyle={{ fontSize: '12px', lineHeight: '18px' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CallTopicsChart;
